import { useEffect, useState } from "react";
import { BanIcon, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONSENT_EVENT, isTrackingOptedOut, setTrackingOptOut } from "@/lib/consent";

/**
 * Prominent, permanent opt-out from download tracking / attribution.
 * Takes effect immediately: the local marker is deleted and detached from
 * all stored attribution data on the server.
 */
export const DownloadTrackingOptOut = () => {
  const [optedOut, setOptedOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setOptedOut(isTrackingOptedOut());
    const sync = () => setOptedOut(isTrackingOptedOut());
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, []);

  const toggle = async (enabled: boolean) => {
    setBusy(true);
    await setTrackingOptOut(enabled);
    setOptedOut(enabled);
    setDone(enabled);
    setBusy(false);
  };

  return (
    <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-6">
      <div className="flex items-start gap-3 mb-3">
        <BanIcon className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-bold text-foreground">
            Download-Tracking dauerhaft deaktivieren (Opt-out)
          </h3>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Mit einem Klick deaktivieren Sie die Download-Statistik und die Zuordnung von Downloads zu
            Kontakt- oder Terminanfragen für diesen Browser dauerhaft. Ihre pseudonyme Kennung wird
            gelöscht und sofort aus allen bereits gespeicherten Zuordnungsdaten entfernt – bestehende
            Downloads erscheinen danach nur noch als anonyme Gesamtzahl. Die Einstellung bleibt auch
            dann bestehen, wenn Sie Analyse-Cookies später erneut zulassen.
          </p>
        </div>
      </div>

      {optedOut ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm font-semibold text-destructive flex items-center gap-2">
            <Check className="w-4 h-4" /> Opt-out ist aktiv – es wird kein Download gezählt und keine
            Zuordnung gespeichert.
          </p>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => toggle(false)}>
            Opt-out aufheben
          </Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button variant="destructive" disabled={busy} onClick={() => toggle(true)}>
            {busy ? "Wird deaktiviert …" : "Download-Tracking jetzt deaktivieren"}
          </Button>
          {done && <span className="text-sm text-muted-foreground">Gespeichert.</span>}
        </div>
      )}
    </div>
  );
};
